import { useState, useEffect } from "react";
import { setupConnectivityListeners, processQueue, getPendingCount } from "../utils/offline-queue";
import * as db from "../lib/db";
import { useToast } from "./useToast";

export function useConnectivity() {
  const toast = useToast();
  const [isOnline, setIsOnline] = useState(
    () => typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const [pendingSyncs, setPendingSyncs] = useState(() => getPendingCount());

  useEffect(() => {
    const flush = () => {
      const count = getPendingCount();
      if (count === 0) return;
      setPendingSyncs(count);
      processQueue({
        meal: (p) => db.insertMealEntry(p.userId, p.date, p.entry),
        workout: (p) => db.insertWorkout(p.userId, p.workout),
        weight: (p) => db.insertWeightEntry(p.userId, p.entry),
        run: (p) => db.insertRun(p.userId, p.run),
      }).then(({ processed, expired }) => {
        setPendingSyncs(getPendingCount());
        if (processed > 0)
          toast.success(`Synced ${processed} offline change${processed > 1 ? "s" : ""}`);
        if (expired > 0)
          toast.warning(
            `${expired} offline change${expired > 1 ? "s were" : " was"} too old (7+ days) and could not be synced`
          );
      });
    };

    const cleanup = setupConnectivityListeners(
      () => {
        setIsOnline(true);
        toast.success("Back online");
        flush();
      },
      () => {
        setIsOnline(false);
        toast.warning("You're offline — changes will be saved locally");
      }
    );

    // Cold start while already online fires no `online` event, so the queue
    // would never drain (and entries expire after 7 days). Flush once on mount.
    if (typeof navigator === "undefined" || navigator.onLine) flush();

    return cleanup;
  }, [toast]);

  return { isOnline, pendingSyncs, setPendingSyncs };
}
